import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Save } from "lucide-react";
import { consultApi, consultError } from "../lib/consult";
import { ButtonRing, PageLoader } from "../components/LoadingRing";

const STEPS = ["Basics", "Your Concern", "Details", "Medical History", "Consent"];
const SECTION = { 1: "basics", 2: "concern", 3: "questions", 4: "history", 5: "consent" };

const FALLBACK_QUESTIONS = [
    { id: "q_sleep", label: "How is your sleep?", field_type: "select", options: ["Good", "Disturbed", "Very poor"] },
    { id: "q_digestion", label: "How is your digestion / appetite?", field_type: "select", options: ["Good", "Irregular", "Poor"] },
    { id: "q_stress", label: "Current stress level", field_type: "select", options: ["Low", "Moderate", "High"] },
    { id: "q_activity", label: "Physical activity per week", field_type: "select", options: ["None", "1-2 days", "3-5 days", "Daily"] },
    { id: "q_diet", label: "Diet type", field_type: "select", options: ["Vegetarian", "Non-vegetarian", "Eggetarian", "Vegan"] },
    { id: "q_water", label: "Daily water intake (litres)", field_type: "text" },
];

const inputCls = "w-full bg-bg0 border border-white/12 text-white px-3 py-3 text-sm outline-none focus:border-gold transition-colors placeholder:text-zinc-600";
const Label = ({ children }) => <span className="mono text-[10px] tracking-[0.18em] text-zinc-400">{children}</span>;
const Text = ({ label, value, onChange, testId, placeholder = "", rows = 0 }) => (
    <label className="block">
        <Label>{label}</Label>
        {rows ? (
            <textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)}
                data-testid={testId} placeholder={placeholder} className={`${inputCls} mt-1.5`} />
        ) : (
            <input value={value || ""} onChange={(e) => onChange(e.target.value)}
                data-testid={testId} placeholder={placeholder} className={`${inputCls} mt-1.5`} />
        )}
    </label>
);
const Choice = ({ label, value, onChange, options, testId }) => (
    <label className="block">
        <Label>{label}</Label>
        <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}
            className={`${inputCls} mt-1.5`}>
            <option value="">Select…</option>
            {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
    </label>
);

export default function ConsultationIntake() {
    const { bookingId } = useParams();
    const nav = useNavigate();
    const [step, setStep] = useState(1);
    const [booking, setBooking] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [data, setData] = useState({ basics: {}, concern: {}, questions: {}, history: {}, consent: {} });
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        consultApi.get(`/me/bookings/${bookingId}`).then(({ data: res }) => {
            const b = res.booking;
            setBooking(b);
            setQuestions(res.questions?.length ? res.questions : FALLBACK_QUESTIONS);
            const intake = b.intake || {};
            setData({
                basics: intake.basics || { name: b.member_name || b.patient_name, age: b.patient_age, gender: b.patient_gender, city: b.patient_city },
                concern: intake.concern || {},
                questions: intake.questions || {},
                history: intake.history || {},
                consent: intake.consent || {},
            });
            setStep(Math.min(5, (intake.step_completed || 0) + 1));
        }).catch((e) => {
            toast.error(consultError(e));
            nav("/account/consultations");
        }).finally(() => setLoading(false));
    }, [bookingId, nav]);

    const set = (section, patch) => setData((d) => ({ ...d, [section]: { ...d[section], ...patch } }));
    const q = (key) => data.questions[key];

    const persist = async (s, submit = false) => {
        const { data: res } = await consultApi.put(`/me/bookings/${bookingId}/intake`, {
            step: s, data: data[SECTION[s]], submit,
        });
        setBooking(res);
    };

    const next = async () => {
        if (step === 2 && !(data.concern.main_concern || "").trim()) return toast.error("Please describe your main concern");
        setBusy(true);
        try {
            await persist(step);
            setStep((v) => Math.min(5, v + 1));
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (e) { toast.error(consultError(e)); } finally { setBusy(false); }
    };

    const saveLater = async () => {
        setBusy(true);
        try {
            await persist(step);
            toast.success("Saved — you can finish this later from My Consultations");
            nav("/account/consultations");
        } catch (e) { toast.error(consultError(e)); } finally { setBusy(false); }
    };

    const submit = async () => {
        if (!data.consent.consent) return toast.error("Please accept the consultation consent to submit");
        setBusy(true);
        try {
            await persist(5, true);
            nav(`/consultation/success/${bookingId}`, { replace: true });
        } catch (e) { toast.error(consultError(e)); } finally { setBusy(false); }
    };

    if (loading) return <PageLoader label="LOADING INTAKE…" />;

    return (
        <div className="pt-28 md:pt-32 pb-24" data-testid="consultation-intake-page">
            <div className="mx-auto max-w-3xl px-6 md:px-10">
                <div className="eyebrow mb-3">Patient intake · {booking?.consultation_id}</div>
                <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight">{STEPS[step - 1]}</h1>
                <p className="mt-4 text-sm text-zinc-400 max-w-xl">
                    Your answers reach only {booking?.doctor_name}. Step {step} of 5 — you can save and finish later at any point.
                </p>

                <div className="mt-7 flex items-center gap-2" data-testid="intake-progress">
                    {STEPS.map((s, i) => (
                        <div key={s} className="flex-1">
                            <div className={`h-[3px] ${i < step ? "bg-gold" : "bg-white/12"}`} />
                            <div className={`mono text-[9px] tracking-[0.14em] mt-2 ${i < step ? "text-gold" : "text-zinc-600"}`}>
                                {s.toUpperCase()}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-10 space-y-5">
                    {step === 1 && (
                        <div className="grid sm:grid-cols-2 gap-4" data-testid="intake-step-1">
                            <Text label="PATIENT NAME" value={data.basics.name} onChange={(v) => set("basics", { name: v })} testId="intake-name" />
                            <Text label="AGE" value={data.basics.age} onChange={(v) => set("basics", { age: v })} testId="intake-age" />
                            <Choice label="GENDER" value={data.basics.gender} onChange={(v) => set("basics", { gender: v })}
                                options={["Male", "Female", "Other"]} testId="intake-gender" />
                            <Choice label="MARITAL STATUS" value={data.basics.marital_status} onChange={(v) => set("basics", { marital_status: v })}
                                options={["Single", "Married", "Other"]} testId="intake-marital" />
                            <Text label="HEIGHT (CM)" value={data.basics.height} onChange={(v) => set("basics", { height: v })} testId="intake-height" />
                            <Text label="WEIGHT (KG)" value={data.basics.weight} onChange={(v) => set("basics", { weight: v })} testId="intake-weight" />
                            <Text label="CITY" value={data.basics.city} onChange={(v) => set("basics", { city: v })} testId="intake-city" />
                            <Text label="OCCUPATION" value={data.basics.occupation} onChange={(v) => set("basics", { occupation: v })} testId="intake-occupation" />
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-4" data-testid="intake-step-2">
                            <Text label="MAIN CONCERN *" rows={4} value={data.concern.main_concern}
                                onChange={(v) => set("concern", { main_concern: v })} testId="intake-concern"
                                placeholder="Describe what you are experiencing in your own words" />
                            <div className="grid sm:grid-cols-2 gap-4">
                                <Choice label="HOW LONG HAS THIS BEEN GOING ON?" value={data.concern.duration}
                                    onChange={(v) => set("concern", { duration: v })} testId="intake-duration"
                                    options={["Less than a month", "1-6 months", "6-12 months", "More than a year", "Several years"]} />
                                <Choice label="HOW MUCH IS IT AFFECTING DAILY LIFE?" value={data.concern.severity}
                                    onChange={(v) => set("concern", { severity: v })} testId="intake-severity"
                                    options={["Mild", "Moderate", "Severe"]} />
                                <Choice label="TREATED FOR THIS BEFORE?" value={data.concern.treated_before}
                                    onChange={(v) => set("concern", { treated_before: v })} testId="intake-treated"
                                    options={["No", "Yes — allopathic", "Yes — Unani / Ayurveda", "Yes — multiple systems"]} />
                                <Choice label="ANY DIAGNOSTIC REPORTS AVAILABLE?" value={data.concern.reports_available}
                                    onChange={(v) => set("concern", { reports_available: v })} testId="intake-reports"
                                    options={["No", "Yes — I will upload", "Yes — older than a year"]} />
                            </div>
                            <Text label="WHAT OUTCOME MATTERS MOST TO YOU?" rows={3} value={data.concern.goal}
                                onChange={(v) => set("concern", { goal: v })} testId="intake-goal" />
                        </div>
                    )}

                    {step === 3 && (
                        <div className="space-y-4" data-testid="intake-step-3">
                            <p className="text-sm text-zinc-400">
                                {booking?.area_name ? `Questions specific to ${booking.area_name}.` : "A few details about your routine."}
                            </p>
                            {questions.map((qq) => {
                                const key = qq.id;
                                const opts = qq.options || [];
                                if (["select", "radio"].includes(qq.field_type) && opts.length) {
                                    return <Choice key={key} label={qq.label.toUpperCase()} value={q(key)} options={opts}
                                        onChange={(v) => set("questions", { [key]: v })} testId={`intake-q-${key}`} />;
                                }
                                if (qq.field_type === "yesno") {
                                    return <Choice key={key} label={qq.label.toUpperCase()} value={q(key)} options={["Yes", "No"]}
                                        onChange={(v) => set("questions", { [key]: v })} testId={`intake-q-${key}`} />;
                                }
                                return <Text key={key} label={qq.label.toUpperCase()} value={q(key)} rows={qq.field_type === "textarea" ? 3 : 0}
                                    onChange={(v) => set("questions", { [key]: v })} testId={`intake-q-${key}`} />;
                            })}
                        </div>
                    )}

                    {step === 4 && (
                        <div className="space-y-4" data-testid="intake-step-4">
                            <Text label="CURRENT MEDICINES / SUPPLEMENTS" rows={3} value={data.history.medications}
                                onChange={(v) => set("history", { medications: v })} testId="intake-medications"
                                placeholder="Name and dose, if known" />
                            <Text label="KNOWN ALLERGIES" rows={2} value={data.history.allergies}
                                onChange={(v) => set("history", { allergies: v })} testId="intake-allergies" />
                            <Text label="EXISTING CONDITIONS" rows={2} value={data.history.conditions}
                                onChange={(v) => set("history", { conditions: v })} testId="intake-conditions"
                                placeholder="Diabetes, blood pressure, thyroid, etc." />
                            <Text label="PAST SURGERIES / HOSPITALISATION" rows={2} value={data.history.surgeries}
                                onChange={(v) => set("history", { surgeries: v })} testId="intake-surgeries" />
                            <Text label="FAMILY HISTORY" rows={2} value={data.history.family_history}
                                onChange={(v) => set("history", { family_history: v })} testId="intake-family" />
                            <div className="grid sm:grid-cols-2 gap-4">
                                <Choice label="SMOKING / TOBACCO" value={data.history.tobacco} options={["Never", "Occasionally", "Daily", "Quit"]}
                                    onChange={(v) => set("history", { tobacco: v })} testId="intake-tobacco" />
                                <Choice label="ALCOHOL" value={data.history.alcohol} options={["Never", "Occasionally", "Regularly", "Quit"]}
                                    onChange={(v) => set("history", { alcohol: v })} testId="intake-alcohol" />
                            </div>
                        </div>
                    )}

                    {step === 5 && (
                        <div className="space-y-5" data-testid="intake-step-5">
                            <Text label="A PERSONAL MESSAGE FOR YOUR CONSULTANT (OPTIONAL)" rows={4} value={data.consent.message}
                                onChange={(v) => set("consent", { message: v })} testId="intake-message"
                                placeholder="Anything you would like to share privately" />
                            <label className="flex items-start gap-3 p-5 border border-white/12 bg-bg1 cursor-pointer">
                                <input type="checkbox" checked={!!data.consent.consent} data-testid="intake-consent"
                                    onChange={(e) => set("consent", { consent: e.target.checked })}
                                    className="mt-1 accent-[#E5A900] w-4 h-4" />
                                <span className="text-xs text-zinc-300 leading-relaxed">
                                    I confirm the information above is accurate to the best of my knowledge. I understand this is a
                                    wellness and traditional-medicine consultation offering guidance on diet, lifestyle and supportive
                                    care — it is not an emergency service, does not replace my treating physician, and no specific
                                    outcome or cure is promised. I consent to my consultant reviewing my details and reports.
                                </span>
                            </label>
                            <button onClick={submit} disabled={busy} data-testid="intake-submit-button"
                                className="w-full btn-pill btn-primary justify-center">
                                {busy ? <ButtonRing label="SUBMITTING…" />
                                    : <><CheckCircle2 className="w-4 h-4 mr-2 inline" /> Submit Intake</>}
                            </button>
                        </div>
                    )}
                </div>

                <div className="mt-10 flex flex-wrap items-center justify-between gap-3">
                    <button onClick={() => (step === 1 ? nav("/account/consultations") : setStep(step - 1))}
                        data-testid="intake-back-button" className="btn-pill btn-secondary">
                        <ArrowLeft className="w-3.5 h-3.5 mr-2 inline" /> {step === 1 ? "Exit" : "Previous"}
                    </button>
                    <div className="flex items-center gap-3">
                        <button onClick={saveLater} disabled={busy} data-testid="intake-save-later-button"
                            className="btn-pill btn-secondary">
                            <Save className="w-3.5 h-3.5 mr-2 inline" /> Save & finish later
                        </button>
                        {step < 5 && (
                            <button onClick={next} disabled={busy} data-testid="intake-next-button" className="btn-pill btn-primary">
                                Continue <ArrowRight className="w-3.5 h-3.5 ml-2 inline" />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
